import { gql } from "@apollo/client";
import { i18nFields } from "./utils";

const bookingInfoGqlQuery = `
bookingInfo {
  closingDays
  periods {
    fromDateInIso
    isClosed
    weekSlots {
      monday {
        allDay
        ranges {
          from
          to
          advanceInMinutes
        }
        maxUnits
      }
      tuesday {
        allDay
        ranges {
          from
          to
          advanceInMinutes
        }
        maxUnits
      }
      wednesday {
        allDay
        ranges {
          from
          to
          advanceInMinutes
        }
        maxUnits
      }
      thursday {
        allDay
        ranges {
          from
          to
          advanceInMinutes
        }
        maxUnits
      }
      friday {
        allDay
        ranges {
          from
          to
          advanceInMinutes
        }
        maxUnits
      }
      saturday {
        allDay
        ranges {
          from
          to
          advanceInMinutes
        }
        maxUnits
      }
      sunday {
        allDay
        ranges {
          from
          to
          advanceInMinutes
        }
        maxUnits
      }
    }
  }
  durations
  intervalBetweenStartsInMinutes
  defaultAdvanceInMinutes
  useServiceItemDuration
}
`;

const commonBookingReservationGqlFields = `
utcStart
utcEnd
units
createMode
bookingTexts {
  startText
  endText
  rangeText
}
`;

const PaymentConfigQuery: string = `
instalmentsPaymentConfigs {
  stripe {
    methods
    publicKey
    privateKey
  }
}
`;

export const QUERIES = {
  AMENITIES: gql`
    query Amenities($buildingId: String) {
      amenities(buildingId: $buildingId) {
        id
        name
        categoryName
        summaryInfo
        active
        color
        building {
          id
          name
        }
        airKeyLockId
        sofiaLockTag
        alertEmail
        isGuestParking
        isOnboardingAppointment
        liability {
          url
          name
        }
        ${bookingInfoGqlQuery}
        ${i18nFields("name", "categoryName", "summaryInfo", "description")}
      }
    }
  `,
  AMENITIES_COLORS: gql`
    query AmenitiesColors($buildingId: String) {
      amenities(buildingId: $buildingId) {
        id
        name
        color
      }
    }
  `,
  AMENITY_CURRENT_SLOTS: gql`
    query AmenitiesCurrentSlots(
      $serviceDefinitionId: String!
      $from: DateTime!
      $to: DateTime!
    ) {
      amenityCurrentSlots(
        serviceDefinitionId: $serviceDefinitionId
        from: $from
        to: $to
      ) {
        key
        date
        serviceDefinitionId
        serviceDefinitionName
        building {
          id
          name
        }
        maxUnits
        availableUnits
        fromTime
        toTime
        durationInMinutes
      }
    }
  `,
  AMENITY_CURRENT_SLOTS_RANGES: gql`
    query AmenitiesCurrentSlotsRanges(
      $serviceDefinitionId: String!
      $from: DateTime!
      $to: DateTime!
      $amountOfPersons: Int!
      $minConsecutiveSlots: Int!
      $esclusive: Boolean
    ) {
      amenityCurrentSlotsRanges(
        serviceDefinitionId: $serviceDefinitionId
        from: $from
        to: $to
        amountOfPersons: $amountOfPersons
        minConsecutiveSlots: $minConsecutiveSlots
        esclusive: $esclusive
      ) {
        fromTime
        toTime
        available
        slots {
          key
          date
          serviceDefinitionId
          serviceDefinitionName
          building {
            id
            name
          }
          maxUnits
          availableUnits
          fromTime
          toTime
          durationInMinutes
        }
      }
    }
  `,
  AMENITY_FIND_AVAILABLE_SLOTS: gql`
    query AmenityBookingFindAvailableSlots(
      $serviceId: String!
      $isoDate: String!
      $durationInMinutes: Int!
    ) {
      amenityBookingFindAvailableSlots(
        serviceId: $serviceId
        isoDate: $isoDate
        durationInMinutes: $durationInMinutes
      ) {
        isoDate
        hour
        unitsAvailable
        displayValue
      }
    }
  `,
  AMENITIES_DEFINITIONS: gql`
    query AmenitiesDefinitions($includeHidden: Boolean) {
      amenitiesDefinitions(
        includeHidden: $includeHidden
        fillAirKeyInfo: false
      ) {
        id
        building {
          id
          name
        }
        hidden
        serviceId
        # serviceCategoryName
        name
        ${i18nFields("name")}
        disclaimers {
          ${i18nFields("title", "description")}
        }
        periodFrom
        periodTo
        weekSlots {
          fromTimeInMinutes
          toTimeInMinutes
          slotDetail {
            name
            durationInMinutes
            maxUnits
          }
        }
        slotVariations {
          date
          daySlot {
            fromTimeInMinutes
            toTimeInMinutes
            slotDetail {
              name
              durationInMinutes
              maxUnits
            }
          }
        }
        exclusiveReservationSettings {
          enabled
          minConsecutiveSlots
          maxConsecutiveSlots
          ${i18nFields("notes")}
        }
        alwaysEsclusive
        minConsecutiveSlots
        maxConsecutiveSlots
        slotDurationInMinutes
        imageUrl
        image {
          url
          blurHash
        }
        active
        isGuestParking
        isOnboardingAppointment
      }
    }
  `,
  AMENITY_DURATIONS: gql`
    query AmenityDurations($serviceId: String!) {
      amenityDurations(serviceId: $serviceId)
    }
  `,
  AMENITY_RESERVATIONS: gql`
    query AmenityReservations(
      $from: DateTime!
      $to: DateTime!
      $serviceId: String
      $serviceDefinitionId: String
      $apartmentIds: [String]
      $limit: Int
      $skip: Int
      $excludeDeleted: Boolean
    ) {
      amenityReservations(
        from: $from
        to: $to
        serviceId: $serviceId
        serviceDefinitionId: $serviceDefinitionId
        apartmentIds: $apartmentIds
        limit: $limit
        skip: $skip
        excludeDeleted: $excludeDeleted
      ) {
        id
        code
        date
        startDate
        serviceDefinitionId
        building {
          id
          name
        }
        apartment {
          id
          code
        }
        createdBy {
          id
          name
          phone
          email
        }
        targetUser {
          id
          name
          phone
          email
        }
        service {
          id
          name
        }
        units
        esclusive
        deletionDate
        deletedBy {
          id
          name
        }
        slots {
          key
          fromTime
          toTime
          durationInMinutes
        }
        didntShowUp
        customerSatisfaction {
          id
          rating
        }
        guestParkingData {
          guestNickname
          guestPlate
        }
        notes
        ${commonBookingReservationGqlFields}
      }
    }
  `,
  APP_VERSIONS: gql`
    query AppVersions($platform: String) {
      appVersions(platform: $platform) {
        id
        platform
        rawVersion
        version
        buildNumber
        mustUpdate
        deprecated
        createdAt
      }
    }
  `,
  SERVICE_CURRENT_SLOT_RANGES: gql`
    query ServiceCurrentSlotRanges(
      $targetUserId: String
      $scope: Int
      $reservationToMoveId: String
      $serviceId: String!
      $date: DateTime!
      $serviceTypeListItems: [ServiceTypeListItemInput]
      $requestedPlace: ServiceCompatiblePlaceInput
    ) {
      serviceCurrentSlotsRanges(
        targetUserId: $targetUserId
        scope: $scope
        reservationToMoveId: $reservationToMoveId
        serviceId: $serviceId
        date: $date
        serviceTypeListItems: $serviceTypeListItems
        requestedPlace: $requestedPlace
      ) {
        reservable
        slots {
          key
          fromTime
          toTime
          durationInMinutes
          date
          serviceId
          reservable
        }
        amenitySlots {
          key
          date
          serviceDefinitionId
          serviceDefinitionName
          building {
            id
            name
          }
          maxUnits
          availableUnits
          fromTime
          toTime
          durationInMinutes
        }
        fromTime
        toTime
      }
    }
  `,
  SERVICE_RESERVATIONS: gql`
    query ServiceReservations(
      $from: DateTime!
      $to: DateTime!
      $serviceId: String
    ) {
      serviceReservations(from: $from, to: $to, serviceId: $serviceId) {
        id
        code
        date
        startDate
        building {
          id
          code
          name
        }
        apartment {
          id
          code
        }
        service {
          id
          name
          providedBy {
            id
            name
          }
          type
          paymentMode
        }
        serviceTypeListItems {
          id
          label
          price
          categories
          type
          quantity
        }
        totalPrice
        rectifiedPrice
        status
        slots {
          key
          fromTime
          toTime
          durationInMinutes
          reservationOptions {
            listItemCategoryName
            bookableUnits
          }
        }
        deletionDate
        createdBy {
          id
          name
        }
        targetUser {
          id
          name
        }
        userDetails {
          email
          firstName
          lastName
          phone
        }
        deletedBy {
          id
          name
        }
        additionalDetails
        vehicle {
          id
          name
          model
          plate
          box
        }
        invoiceRequested
        linkedAmenityReservation {
          id
          amenityDefinitionId
          code
          date
          startDate
          service {
            id
            name
          }
          slots {
            key
            fromTime
            toTime
            durationInMinutes
          }
        }
        externalClientReservationData {
          details {
            orderCode
            estimatedDeliveryDate
            estimatedPickupDate
            pickedItems
            totalPrice
            paymentStatus
          }
        }
        customerSatisfaction {
          id
          rating
        }
        pushMessage
        lastTimeForPayment
        survey {
          question
          answer
        }
        ${commonBookingReservationGqlFields}
        orderCoupon {
          code
        }
      }
    }
  `,
  UNAVAILABLE_SLOTS: gql`
    query ServiceUnavailableSlots($serviceId: String, $dateFrom: Int) {
      serviceUnavailableSlots(serviceId: $serviceId, dateFrom: $dateFrom) {
        id
        serviceId
        service {
          i18n {
            name_it
          }
        }
        blockedFrom
        blockedTo
        blockedUnits
        notes
      }
    }
  `,
  APARTMENTS: gql`
    query Apartments($buildingId: String) {
      apartments(buildingId: $buildingId) {
        id
        name
        code
        floorInfo
        building {
          id
          code
          name
        }
        users {
          id
          name
          role
          permissions {
            canReserveAmenities
            domotics {
              lights
              clima
              alarm
              shutters
            }
          }
        }
        owner {
          id
          name
          email
          phone
        }
        reservationConductStatus {
          lastDidntShowUpBanDate
          lastUnlockDate
          blocked
        }
        reservationOptions {
          service {
            id
            name
          }
          maxUnits
        }
        domoticsProvider
        wiserOpts {
          wiserUrl
          getGeneralAlarmAddress
          setGeneralAlarmAddress
          setGeneralFanAddress
          setGeneralLightsAddress
          setGeneralShuttersMoveAddress
          setGeneralShuttersStatusAddress
          getWinterSummerAddress
          setWinterSummerAddress
        }
        shouldCompleteSurvey
        lastSurveyId
        lastSurveyCompletedDate
        underConstruction
        tag
        onboarding {
          docsAndContracts {
            description
            ${i18nFields("description")}
            docs {
              title
              ${i18nFields("title")}
              file {
                url
                name
              }
            }
            timeline {
              hiddenDate
              textDate
              description
              ${i18nFields("textDate", "description")}
              gallery {
                title
                description
                ${i18nFields("title", "description")}
                photos {
                  url
                }
                attachments {
                  url
                  name
                }
              }
            }
          }
        }
      }
    }
  `,
  APARTMENT_INFO: gql`
    query ApartmentInfo($id: String!) {
      apartmentInfo(id: $id) {
        id
        name
        code
        building {
          id
          name
        }
        users {
          id
          name
          role
        }
        owner {
          id
          name
          email
          phone
        }
        reservationConductStatus {
          lastDidntShowUpBanDate
          lastUnlockDate
          blocked
        }
        reservationOptions {
          service {
            id
            name
          }
          maxUnits
        }
        domoticsProvider
        underConstruction
      }
    }
  `,
  BUILDINGS: (mode: "full" | "light") => gql`
    query Buildings {
      buildings {
        id,
        name,
        code,
        ${
          mode === "full"
            ? `
          primaryColor,
          logo,
          mainPhone,
          latitude,
          longitude,
          zipCode,
          hasElevator,
          showInstalments,
          enableInstalments,
          rulesDoc {
            url, name
          },
          otherAttachments {
            url, name
          },
          faqs {
            ${i18nFields("category")},
            items {
              ${i18nFields("label", "description")}
            }
          }
          laundryOpts {
            externalZoneId
            pickupTimeSlots
            deliveryTimeSlots
            cityName
            shippingInfo {
              name
              addressLine1
              streetNumber
              city
              province
              cap
              extraInformations
              mobileNumber
              companyName
            }
            preferredPickupTimeInUtcHours
            forbiddenDates
          }
          disableDomotics
          airKeyApiKey
          onboarding {
            callToActionLink
            ${i18nFields(
              "callToActionTitle",
              "specificationsText",
              "worksiteText",
              "introText"
            )}
            generalSpecifications {
              url
              name
            }
            worksiteTimeline {
              hiddenDate
              textDate
              description
              ${i18nFields("textDate", "description")}
              gallery {
                title
                description
                ${i18nFields("title", "description")}
                photos {
                  url
                }
              }
            }
          }
          guestParking {
            parkingCodes
            devices {
              type
              baseUrl
              username
              password
              channel
            }
          }
          ${PaymentConfigQuery}
        `
            : ""
        }
      }
    }
  `,
  BUILDING_MEDIA: gql`
    query BuildingMedia($buildingId: String!) {
      buildingMedia(buildingId: $buildingId) {
        id
        building {
          id
          code
        }
        mediaType
        image {
          url
          blurHash
        }
        roomMetadata {
          roomType
        }
      }
    }
  `,
  BUILDING_DEFAULT_ROOM_MEDIA_PICTURE: gql`
    query BuildingDefaultRoomMediaPicture($buildingId: String!) {
      buildingDefaultRoomMediaPicture(buildingId: $buildingId) {
        id
        building {
          id
          code
        }
        mediaType
        image {
          url
          blurHash
        }
        roomMetadata {
          roomType
        }
      }
    }
  `,
  BUILDING_ROOM_MEDIA_PICTURES: gql`
    query BuildingRoomMediaRoomPictures($buildingId: String!) {
      buildingRoomMediaRoomPictures(buildingId: $buildingId) {
        id
        building {
          id
          code
        }
        mediaType
        image {
          url
          blurHash
        }
        roomMetadata {
          roomType
        }
      }
    }
  `,
  BUILDING_ROOM_MEDIA_ICON_PICTURES: gql`
    query BuildingRoomMediaIconPictures($buildingId: String!) {
      buildingRoomMediaIconPictures(buildingId: $buildingId) {
        id
        building {
          id
          code
        }
        mediaType
        image {
          url
          blurHash
        }
        roomMetadata {
          roomType
        }
      }
    }
  `,
  COMMUNICATION_DETAILS: gql`
    query CommunicationDetail($id: String!) {
      communicationDetail(id: $id) {
        id
        createdAt
        user {
          name
        }
        communication {
          name
        }
        apartment {
          code
        }
        readDate
        pushNotifications {
          token
          sendDate
        }
      }
    }
  `,
  COMMUNICATIONS: gql`
    query Communications(
      $communicationGroupsIds: [String]
      $apartmentIds: [String]
      $userIds: [String]
      $name: String
      $subject: String
      $draft: Boolean
    ) {
      communications(
        first: 100,
        communicationGroupsIds: $communicationGroupsIds
        apartmentIds: $apartmentIds
        userIds: $userIds
        name: $name
        subject: $subject
        draft: $draft
      ) {
        id
        name
        subject
        message
        ${i18nFields("subject", "message")}
        draft
        buildingIds
        targets {
          userId
          apartmentId
          apartmentCode
          apartmentName
          name
          email
        }
        groups {
          id
          name
          color
        }
        attachments {
          url
          name
        }
        isCommercial
      }
    }
  `,
  COMMUNICATION_GROUPS: gql`
    query CommunicationGroups($apartmentIds: [String], $name: String) {
      communicationGroups(apartmentIds: $apartmentIds, name: $name) {
        id
        name
        color
        description
        apartments {
          id
          name
          code
        }
        users {
          id
          fullName
          username
        }
      }
    }
  `,
  CONTACTS: gql`
    query Contacts {
      contacts {
        id
        buildings {
          id
          code
        }
        label
        description
        emailAddresses
        phoneNumbers
        ${i18nFields("label", "description")}
      }
    }
  `,
  CUSTOMER_SATISFACTIONS: gql`
    query CustomerSatisfactions($type: String) {
      customerSatisfactions(type: $type) {
        id
        building {
          id
          code
        }
        apartment {
          id
          code
        }
        user {
          id
          fullName
        }
        amenityReservation {
          id
          code
          service {
            id
            name
          }
          date
          startDate
        }
        serviceReservation {
          id
          code
          service {
            id
            name
          }
          date
          startDate
          serviceProvidedBy {
            id
            name
          }
        }
        notes
        rating
        closedAt
        createdAt
        updatedAt
      }
    }
  `,
  BACKOFFICE_COUPONS: gql`
    query BackofficeCoupons($apartmentId: String, $buildingId: String, $serviceId: String, $expired: Boolean, $type: String) {
      coupons(apartmentId: $apartmentId, buildingId: $buildingId, serviceId: $serviceId, expired: $expired, type: $type) {
        id
        type
        targetBuildings {
          id
          code
        }
        targetApartments {
          id
          code
        }
        targetUsers {
          id
          fullName
        }
        service {
          id
          name
        }
        code
        discountInPercentage
        discountInPrice
        listItems {
          id
          label
        }
        consumedByApartments {
          apartmentId
          apartmentCode
          userId
          userFullName
        }
        consumedByUsers {
          id
          fullName
        }
        consumedByReservations {
          id
          code
          apartmentId
          userId
        }
        maxUseCount
        expireDate
        createdAt
        updatedAt
        ${i18nFields("description", "termsAndConditions")}
      }
    }
  `,
  DEVICES: gql`
    query Devices(
      $apartmentId: String
      $buildingId: String
      $roomId: String
      $loadCurrentDomoticsValues: Boolean
    ) {
      devices(
        apartmentId: $apartmentId
        buildingId: $buildingId
        roomId: $roomId
        loadCurrentDomoticsValues: $loadCurrentDomoticsValues
      ) {
        id
        name
        type
        categoryName
        room {
          id
          name
        }
        apartment {
          id
          name
          code
        }
        building {
          id
          name
        }
        wiserOpts {
          getLightAddress
          setLightAddress

          getDimmerAddress
          setDimmerAddress

          getStatusAddress
          setStatusAddress

          getMoveAddress
          setMoveAddress

          setFanAddress

          setTemperatureAddressSummer
          setTemperatureAddressWinter
        }
        lightValue
        dimmerLightValue
        shutterDirectionValue
        shutterStatusValue
      }
    }
  `,
  ROOMS: gql`
    query Rooms($apartmentId: String!, $loadCurrentDomoticsValues: Boolean) {
      rooms(
        apartmentId: $apartmentId
        loadCurrentDomoticsValues: $loadCurrentDomoticsValues
      ) {
        id
        name
        image {
          url
          blurHash
        }
        apartment {
          id
          name
          code
          building {
            id
            name
          }
        }
        wiserOpts {
          getTemperatureAddress
          getTemperatureAddressWinter
          setTemperatureAddressWinter
          setTemperatureAddressSummer
          getTemperatureAddressSummer
          getFanAddress
          setFanAddress
          getHumidityAddress
        }
        currentTemperature
        currentHumidity
        currentFan
        currentWinterTemperature
        currentSummerTemperature
      }
    }
  `,
  BACKOFFICE_SERVICES: gql`
    query Services {
      backofficeServices {
        id
        serviceCategories
        featured
        buildings {
          id
          name
          compatiblePlaces {
            type
            amenity {
              id
              name
            }
          }
          overrideProps {
            name
            description
            phone
            image {
              url
              blurHash
            }
            attachments {
              url
              name
            }
            type
            infoBoxContent
            reservationDeleteConditions
            reservationUsefulInfoBoxContent
            stripePrivateApiKey
            stripePublicApiKey
            active
            displayInApp
          }
        }
        name
        description
        phone
        image {
          url
          blurHash
        }
        attachments {
          url
          name
        }
        type
        infoBoxContent
        weekSlots {
          reservationTimeOffsetInHours
          fromTime
          toTime
          durationInMinutes
          maxUnits
          reservationOptions {
            listItemCategoryName
            bookableUnits
          }
        }
        daySlotAvailabilityOptions {
          mode
        }
        reservationDeleteConditions
        reservationUsefulInfoBoxContent
        stripePrivateApiKey
        stripePublicApiKey
        providedBy {
          id
          name
        }
        active
        alertEmail
        displayInApp
        ${i18nFields(
          "name",
          "description",
          "infoBoxTitle",
          "infoBoxContent",
          "reservationDeleteConditions",
          "reservationUsefulInfoBoxContent",
          "reservationOkPushMessage",
          "reservationKoPushMessage"
        )}
        privacyPolicyLink
        termsAndConditionLink
        laundryOptions {
          washAndFoldDetailLink
          forbiddenDates
        }
        composableOptions {
          displayServiceDescription
          displayListItemsInHomepage
          displayPhoneNumberCta
          displayAttachments
          askAvailabilitySlots
          askServices
          askProducts
          askVehicleInfo
          askAmenityPlace
          paymentMode
          paymentReloadInPercentage
          ${i18nFields("paymentReloadInPercentageReason")}
        }
        reservationSurvey {
          required,
          ${i18nFields("title")}
        },
        hideSlotEndTime
        bevyTimeSlotInUtc
        ${bookingInfoGqlQuery}
        tacUrl, tacServiceId, tacQuotationId
        sortIndex
      }
    }
  `,
  SERVICE_TYPE_LIST_ITEMS: gql`
    query ServiceTypeListItems($serviceId: String, $type: Int, $apartmentId: String) {
      serviceTypeListItems(serviceId: $serviceId, type: $type, apartmentId: $apartmentId) {
        id
        service {
          id
          name
          providedBy {
            id
            name
          }
          type
        }
        categories
        notes
        label
        description
        imageUrl
        price
        inPromo
        promoPrice
        requiredTime
        type
        order
        targetApartment {
          id
          code
          building {
            id
            code
          }
        }
        minSellableQuantity
        maxSellableQuantity
        ${i18nFields(
          "label",
          "description",
          "notes",
          "categories",
          "prefix",
          "suffix"
        )}
      }
    }
  `,
  SERVICE_TIME_SLOT_VARIATIONS: gql`
    query ServiceTimeSlotVariations($slotKeys: [String!]!) {
      serviceTimeSlotVariations(slotKeys: $slotKeys) {
        id
        serviceId
        key
        maxUnits
        reservationOptions {
          listItemCategoryName
          bookableUnits
        }
      }
    }
  `,
  /**
   * For the specified `serviceId`, this returns the value of the field `daySlotAvailabilityOptions`
   */
  SERVICE_DAY_SLOT_RESERVATION_OPTIONS: gql`
    query ServiceDaySlotReservationOptions($id: String!) {
      backofficeServicesFindOneById(id: $id) {
        daySlotAvailabilityOptions {
          mode
        }
      }
    }
  `,
  SERVICE_WEEK_DAY_SLOTS: gql`
    query ServiceWeekDaySlots($serviceId: String!, $weekDay: Int!) {
      serviceWeekDaySlots(serviceId: $serviceId, weekDay: $weekDay) {
        reservationTimeOffsetInHours
        fromTime
        toTime
        durationInMinutes
        maxUnits
        reservationOptions {
          listItemCategoryName
          bookableUnits
        }
      }
    }
  `,
  SERVICE_TIME_SLOTS_COMPUTE_KEYS: gql`
    query ServiceTimeSlotsComputeKeys(
      $year: Int!
      $month: Int!
      $day: Int!
      $serviceId: String!
      $slots: [ServiceTimeSlotInput!]!
    ) {
      serviceTimeSlotsComputeKeys(
        year: $year
        month: $month
        day: $day
        slots: $slots
        serviceId: $serviceId
      )
    }
  `,
  SERVICE_CATEGORIES: gql`
    query ServiceCategories {
      serviceCategories {
        id
        image {
          url
          name
        }
        icon {
          url
          name
        }
        i18n {
          name_it
          name_en
        }
        sortIndex
      }
    }
  `,
  USERS: gql`
    query Users($role: String, $apartmentIds: [String], $buildingId: String) {
      users(role: $role, apartmentIds: $apartmentIds, buildingId: $buildingId) {
        id
        username
        firstName
        lastName
        email
        phone
        status
        role
        buildings {
          id
          name
          apartments {
            id
            name
            code
            role
          }
        }
        passwordChangeRequired
        language
        latestUserPrivacySettings {
          basicPolicyAccepted
          newsletterAccepted
          thirdPartyDataTransmissionAccepted
          userPersonalDataTransmissionAccepted
          lastUpdate
        }
        lastTokenCheckedDate
        isTestAccount
      }
    }
  `,
  USER_PRIVACY_REGISTRY: gql`
    query UserPrivacyRegistry($userId: String!) {
      userPrivacyRegistry(userId: $userId) {
        basicPolicyAccepted
        newsletterAccepted
        thirdPartyDataTransmissionAccepted
        userPersonalDataTransmissionAccepted
        lastUpdate
      }
    }
  `,
  USER_DEVICES: gql`
    query UserDevices($userId: String!) {
      userDevices(userId: $userId) {
        id
        userId
        platform
        deviceUid
        deviceInfo
        currentVersion
        createdAt
        updatedAt
      }
    }
  `,
};
